import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Sparkles, Plus, Users } from "lucide-react";
import { AiModelPicker, DEFAULT_PICK } from "./AiModelPicker";

const eur = (v) => `€ ${Number(v || 0).toFixed(2)}`;

export default function SegmentCouponAi({ segment, onClose, onCreated }) {
  const [pick, setPick] = useState(DEFAULT_PICK);
  const [goal, setGoal] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [creating, setCreating] = useState(null);

  const generate = async () => {
    setBusy(true); setRes(null);
    try { const { data } = await api.post("/ai/coupon-suggest", { ...pick, segment_id: segment.id, goal: goal || null }); setRes(data); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const create = async (sg, i, assign) => {
    setCreating(`${i}-${assign}`);
    try {
      const { data: cp } = await api.post("/coupons", { name: sg.name, discount_type: sg.discount_type, discount_value: sg.discount_value, min_spend: sg.min_spend, valid_until: sg.valid_until, active: true, single_use: true });
      let msg = `Coupon "${cp.name}" creato (${cp.code})`;
      if (assign) { const { data: a } = await api.post(`/coupons/${cp.id}/assign`, { segment_ids: [segment.id] }); msg += ` e assegnato a ${a.assigned ?? a.count ?? segment.member_count} clienti`; }
      toast.success(msg);
      setRes((r) => ({ ...r, suggestions: r.suggestions.map((x, j) => j === i ? { ...x, created: cp } : x) }));
      onCreated?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setCreating(null); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="segment-coupon-ai">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-400" /> Coupon suggeriti dall'AI · {segment.name}</DialogTitle></DialogHeader>
        <div className="text-xs text-zinc-400 flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {segment.member_count} clienti nel cluster · l'AI legge regole, punti e spesa media, coupon esistenti e catalogo</div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <Input value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="Obiettivo (opzionale): es. riportarli in negozio, alzare lo scontrino…" className="bg-zinc-950 border-zinc-800 h-8 text-sm" data-testid="segment-coupon-ai-goal" />
          </div>
          <AiModelPicker value={pick} onChange={setPick} testid="segment-coupon-ai-pick" />
          <Button size="sm" onClick={generate} disabled={busy} data-testid="segment-coupon-ai-generate" className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
            <Sparkles className={`h-4 w-4 mr-1 ${busy ? "animate-pulse" : ""}`} /> {busy ? "Genero…" : res ? "Rigenera" : "Genera 3 proposte"}
          </Button>
        </div>
        {res && (
          <div className="space-y-2" data-testid="segment-coupon-ai-results">
            {res.suggestions.map((sg, i) => (
              <div key={i} className="rounded-xl border border-zinc-800 p-3 space-y-1.5" data-testid={`segment-coupon-ai-item-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold">{sg.name}</div>
                    <div className="text-xs text-zinc-400">{sg.description}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-heading font-extrabold text-xl text-amber-400">{sg.discount_type === "percent" ? `-${sg.discount_value}%` : `-${eur(sg.discount_value)}`}</div>
                    <div className="text-[10px] text-zinc-500">{sg.min_spend > 0 ? `min ${eur(sg.min_spend)} · ` : ""}{sg.valid_days} gg</div>
                  </div>
                </div>
                <div className="text-[11px] text-zinc-500 italic">💡 {sg.rationale}</div>
                {sg.push_message && <div className="text-[11px] text-zinc-300 bg-zinc-900 rounded-md px-2 py-1">🔔 {sg.push_message}</div>}
                {sg.created ? (
                  <div className="text-xs text-emerald-400" data-testid={`segment-coupon-ai-created-${i}`}>✓ Creato · codice <span className="font-mono">{sg.created.code}</span></div>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={!!creating} onClick={() => create(sg, i, false)} data-testid={`segment-coupon-ai-create-${i}`} className="border-zinc-700"><Plus className="h-3.5 w-3.5 mr-1" /> Crea coupon</Button>
                    <Button size="sm" disabled={!!creating} onClick={() => create(sg, i, true)} data-testid={`segment-coupon-ai-create-assign-${i}`} className="bg-zinc-800"><Users className="h-3.5 w-3.5 mr-1" /> Crea e assegna al cluster</Button>
                  </div>
                )}
              </div>
            ))}
            <div className="text-[10px] text-zinc-500">Generato con {res.model}. I coupon creati sono attivi, monouso, validi fino alla data indicata: puoi modificarli nella pagina Coupon.</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
